// store/patientSessionSlice.ts
import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { patientSessionApi } from '../services/patientApi';

// Types for patient session
export interface PatientSession {
  id?: number;
  session_key: string;
  created_at: string;
  last_activity: string;
  expires_at: string;
  preferred_language: string;
  allow_analytics: boolean;
  allow_follow_up: boolean;
  contact_method: 'none' | 'phone' | 'email' | 'room';
  is_active: boolean;
}

export interface SessionCreateData {
  language?: string;
  allow_analytics?: boolean;
  allow_follow_up?: boolean;
  contact_method?: PatientSession['contact_method'];
}

export interface SessionPreferences {
  preferred_language: string;
  allow_analytics: boolean;
  allow_follow_up: boolean;
  contact_method: PatientSession['contact_method'];
}

// Async thunks
export const createPatientSession = createAsyncThunk(
  'patientSession/create',
  async (data: SessionCreateData = {}, { rejectWithValue }) => {
    try {
      const sessionResponse = await patientSessionApi.createSession(data);
      
      // Store session data in localStorage for persistence
      const sessionToStore = {
        session_key: sessionResponse.session_id,
        expires_at: sessionResponse.expires_at,
        created_at: new Date().toISOString(),
        last_activity: new Date().toISOString(),
        preferred_language: data.language || 'en',
        allow_analytics: data.allow_analytics || false,
        allow_follow_up: data.allow_follow_up || false,
        contact_method: data.contact_method || 'none',
        is_active: true
      };
      localStorage.setItem('patient_session', JSON.stringify(sessionToStore));
      
      return sessionResponse;
    } catch (error: any) {
      return rejectWithValue({ 
        message: error.message || 'Failed to create session',
        status: error.status || 500,
        details: error.details ? JSON.stringify(error.details) : null
      });
    }
  }
);

export const refreshSession = createAsyncThunk(
  'patientSession/refresh',
  async (_, { rejectWithValue }) => {
    try {
      const refreshData = await patientSessionApi.refreshSession();
      return refreshData;
    } catch (error: any) {
      return rejectWithValue({ 
        message: error.message || 'Failed to refresh session',
        details: error
      });
    }
  }
);

export const updateSessionPreferences = createAsyncThunk(
  'patientSession/updatePreferences',
  async (preferences: Partial<SessionPreferences>, { getState, rejectWithValue }) => {
    try {
      const state = getState() as { patientSession: PatientSessionState };
      const sessionId = state.patientSession.currentSession?.session_key;
      
      if (!sessionId) {
        return rejectWithValue({ message: 'No active session' });
      }
      
      const updatedSession = await patientSessionApi.updatePreferences(sessionId, preferences);
      return updatedSession;
    } catch (error: any) {
      return rejectWithValue({ 
        message: error.message || 'Failed to update preferences',
        status: error.status || 500,
        details: error.details ? JSON.stringify(error.details) : null
      });
    }
  }
);

// Session validation and auto-refresh
export const validateSession = createAsyncThunk(
  'patientSession/validate',
  async (_, { rejectWithValue }) => {
    try {
      const sessionData = await patientSessionApi.validateSession();
      return sessionData;
    } catch (error: any) {
      if (error.status === 401 || error.status === 404) {
        // Session expired, invalid, or endpoint not found
        return rejectWithValue({ expired: true });
      }
      return rejectWithValue({ 
        message: error.message || 'Session validation failed',
        status: error.status || 500,
        // Only include serializable details
        details: error.details ? JSON.stringify(error.details) : null
      });
    }
  }
);

// Initial state
interface PatientSessionState {
  currentSession: PatientSession | null;
  sessionId: string | null;
  isActive: boolean;
  expiresAt: string | null;
  
  // Session management
  isCreating: boolean;
  isValidating: boolean;
  isRefreshing: boolean;
  isUpdatingPreferences: boolean;
  
  // Session persistence
  lastActivity: string | null;
  sessionStartTime: string | null;
  activityCount: number;
  
  // Preferences
  preferences: SessionPreferences;
  
  // Error handling
  sessionError: string | null;
  isExpired: boolean;
  needsRefresh: boolean;
  
  // Session analytics (if allowed)
  pageViews: string[];
  timeSpent: number;
  interactions: number;
}

const initialState: PatientSessionState = {
  currentSession: null,
  sessionId: null,
  isActive: false,
  expiresAt: null,
  isCreating: false,
  isValidating: false,
  isRefreshing: false,
  isUpdatingPreferences: false,
  lastActivity: null,
  sessionStartTime: null,
  activityCount: 0,
  preferences: {
    preferred_language: 'en',
    allow_analytics: false,
    allow_follow_up: false,
    contact_method: 'none',
  },
  sessionError: null,
  isExpired: false,
  needsRefresh: false,
  pageViews: [],
  timeSpent: 0,
  interactions: 0,
};

const patientSessionSlice = createSlice({
  name: 'patientSession',
  initialState,
  reducers: {
    // Session activity tracking
    recordActivity: (state, action: PayloadAction<{ type: string; page?: string }>) => {
      state.lastActivity = new Date().toISOString();
      state.activityCount += 1;
      state.interactions += 1;
      
      if (action.payload.page && state.preferences.allow_analytics) {
        state.pageViews.push(action.payload.page);
      }
    },
    
    updateTimeSpent: (state, action: PayloadAction<number>) => {
      if (state.preferences.allow_analytics) {
        state.timeSpent += action.payload;
      }
    },
    
    // Session preferences
    updatePreferences: (state, action: PayloadAction<Partial<SessionPreferences>>) => {
      state.preferences = {
        ...state.preferences,
        ...action.payload,
      };
    },
    
    // Session lifecycle
    markSessionExpired: (state) => {
      state.isExpired = true;
      state.isActive = false;
      state.needsRefresh = true;
    },
    
    markNeedsRefresh: (state) => {
      state.needsRefresh = true;
    },
    
    clearSessionError: (state) => {
      state.sessionError = null;
    },
    
    // Session cleanup
    clearSession: (state) => {
      state.currentSession = null;
      state.sessionId = null;
      state.isActive = false;
      state.expiresAt = null;
      state.lastActivity = null;
      state.sessionStartTime = null;
      state.isExpired = false;
      state.needsRefresh = false;
      state.sessionError = null;
      
      // Clear analytics data
      state.pageViews = [];
      state.timeSpent = 0;
      state.interactions = 0;
      state.activityCount = 0;
    },
    
    // Initialize from stored session
    initializeSession: (state, action: PayloadAction<{
      sessionId: string;
      expiresAt: string;
      preferences?: Partial<SessionPreferences>;
    }>) => {
      const { sessionId, expiresAt, preferences } = action.payload;
      state.sessionId = sessionId;
      state.expiresAt = expiresAt;
      state.isActive = new Date() < new Date(expiresAt);
      state.sessionStartTime = new Date().toISOString();
      
      if (preferences) {
        state.preferences = { ...state.preferences, ...preferences };
      }
      
      // Check if session is close to expiration
      const now = new Date().getTime();
      const expiry = new Date(expiresAt).getTime();
      const timeToExpiry = expiry - now;
      const oneHour = 60 * 60 * 1000;
      
      if (timeToExpiry < oneHour) {
        state.needsRefresh = true;
      }
    },
  },
  
  extraReducers: (builder) => {
    // Create session
    builder
      .addCase(createPatientSession.pending, (state) => {
        state.isCreating = true;
        state.sessionError = null;
      })
      .addCase(createPatientSession.fulfilled, (state, action) => {
        state.isCreating = false;
        state.sessionId = action.payload.session_id;
        state.expiresAt = action.payload.expires_at;
        state.isActive = true;
        state.sessionStartTime = new Date().toISOString();
        state.lastActivity = new Date().toISOString();
        state.isExpired = false;
        state.needsRefresh = false;
        
        // Store session info in localStorage for persistence
        localStorage.setItem('patient_session', JSON.stringify({
          sessionId: action.payload.session_id,
          expiresAt: action.payload.expires_at,
          preferences: state.preferences,
        }));
      })
      .addCase(createPatientSession.rejected, (state, action) => {
        state.isCreating = false;
        state.sessionError = (action.payload as any)?.message || 'Failed to create session';
      })
      
      // Validate session
      .addCase(validateSession.pending, (state) => {
        state.isValidating = true;
        state.sessionError = null;
      })
      .addCase(validateSession.fulfilled, (state, action) => {
        state.isValidating = false;
        state.currentSession = action.payload;
        state.isActive = action.payload.is_active;
        state.expiresAt = action.payload.expires_at;
        state.lastActivity = action.payload.last_activity;
        state.isExpired = false;
        state.needsRefresh = false;
        
        // Update preferences from server
        state.preferences = {
          preferred_language: action.payload.preferred_language,
          allow_analytics: action.payload.allow_analytics,
          allow_follow_up: action.payload.allow_follow_up,
          contact_method: action.payload.contact_method,
        };
      })
      .addCase(validateSession.rejected, (state, action) => {
        state.isValidating = false;
        const error = action.payload as any;
        
        if (error?.expired) {
          state.isExpired = true;
          state.isActive = false;
          state.needsRefresh = true;
          localStorage.removeItem('patient_session');
        } else {
          state.sessionError = error?.message || 'Session validation failed';
        }
      })
      
      // Refresh session
      .addCase(refreshSession.pending, (state) => {
        state.isRefreshing = true;
      })
      .addCase(refreshSession.fulfilled, (state, action) => {
        state.isRefreshing = false;
        state.expiresAt = action.payload.expires_at;
        state.isActive = true;
        state.isExpired = false;
        state.needsRefresh = false;
        state.lastActivity = new Date().toISOString();
        
        // Update stored session
        const stored = localStorage.getItem('patient_session');
        if (stored) {
          const sessionData = JSON.parse(stored);
          sessionData.expiresAt = action.payload.expires_at;
          localStorage.setItem('patient_session', JSON.stringify(sessionData));
        }
      })
      .addCase(refreshSession.rejected, (state, action) => {
        state.isRefreshing = false;
        state.sessionError = (action.payload as any)?.message || 'Failed to refresh session';
        state.isExpired = true;
        state.isActive = false;
      })
      
      // Update preferences
      .addCase(updateSessionPreferences.pending, (state) => {
        state.isUpdatingPreferences = true;
      })
      .addCase(updateSessionPreferences.fulfilled, (state, action) => {
        state.isUpdatingPreferences = false;
        state.currentSession = action.payload;
        state.preferences = {
          preferred_language: action.payload.preferred_language,
          allow_analytics: action.payload.allow_analytics,
          allow_follow_up: action.payload.allow_follow_up,
          contact_method: action.payload.contact_method,
        };
        
        // Update stored preferences
        const stored = localStorage.getItem('patient_session');
        if (stored) {
          const sessionData = JSON.parse(stored);
          sessionData.preferences = state.preferences;
          localStorage.setItem('patient_session', JSON.stringify(sessionData));
        }
      })
      .addCase(updateSessionPreferences.rejected, (state, action) => {
        state.isUpdatingPreferences = false;
        state.sessionError = (action.payload as any)?.message || 'Failed to update preferences';
      });
  },
});

export const {
  recordActivity,
  updateTimeSpent,
  updatePreferences,
  markSessionExpired,
  markNeedsRefresh,
  clearSessionError,
  clearSession,
  initializeSession,
} = patientSessionSlice.actions;

// Selectors
export const selectCurrentSession = (state: { patientSession: PatientSessionState }) => state.patientSession.currentSession;
export const selectIsSessionActive = (state: { patientSession: PatientSessionState }) => state.patientSession.isActive;
export const selectSessionId = (state: { patientSession: PatientSessionState }) => state.patientSession.sessionId;
export const selectSessionPreferences = (state: { patientSession: PatientSessionState }) => state.patientSession.preferences;
export const selectNeedsRefresh = (state: { patientSession: PatientSessionState }) => state.patientSession.needsRefresh;
export const selectSessionError = (state: { patientSession: PatientSessionState }) => state.patientSession.sessionError;
export const selectExpiresAt = (state: { patientSession: PatientSessionState }) => state.patientSession.expiresAt;

export default patientSessionSlice.reducer;